/**
 * Billing Calculations Utility
 * 
 * Core business logic for customer billing calculations
 * Combines period calculations, data fetching, and forecasting
 */

import { LLMCall } from '../API';
import { PeriodData, calculatePeriodBounds, getDefaultPeriodType } from '../utils/periodCalculations';
import { BillingMetrics, calculateBillingMetrics, ForecastOptions } from '../utils/forecastingUtils';
import { fetchLLMCallsByCustomerIdAndPeriod, fetchLLMCallsPreview, CustomerCallsResult, BillingPerformanceMetrics, logBillingPerformance } from '../dataaccess/fetchLLMCallsByCustomerId';
import { fetchOpenRouterCatalog } from '../dataaccess/fetchOpenRouterCatalog';

type OpenRouterPricingInfo = {
  name: string;
  inputTokenPricePerMillion: number | null;
  outputTokenPricePerMillion: number | null;
};

let openRouterPricingCache:
  | { loadedAt: number; byModelId: Map<string, OpenRouterPricingInfo> }
  | null = null;

const OPENROUTER_PRICING_CACHE_TTL_MS = 5 * 60 * 1000;

function parseNanoUsdPerTokenToPerMillion(value?: string | null): number | null {
  if (!value) {
    return null;
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }

  return parsed / 1000;
}

async function getOpenRouterPricingMap(): Promise<Map<string, OpenRouterPricingInfo>> {
  const now = Date.now();

  if (openRouterPricingCache && now - openRouterPricingCache.loadedAt < OPENROUTER_PRICING_CACHE_TTL_MS) {
    return openRouterPricingCache.byModelId;
  }

  const catalog = await fetchOpenRouterCatalog();
  const byModelId = new Map<string, OpenRouterPricingInfo>();

  for (const record of catalog) {
    const id = record?.model?.id;
    if (!id) {
      continue;
    }

    byModelId.set(id, {
      name: record.model.name || id,
      inputTokenPricePerMillion: parseNanoUsdPerTokenToPerMillion(
        record.currentPricing?.promptTokenUsdNano || null
      ),
      outputTokenPricePerMillion: parseNanoUsdPerTokenToPerMillion(
        record.currentPricing?.completionTokenUsdNano || null
      ),
    });
  }

  openRouterPricingCache = {
    loadedAt: now,
    byModelId,
  };

  return byModelId;
}

async function enrichCallsWithOpenRouterPricing(calls: LLMCall[]): Promise<LLMCall[]> {
  const openRouterModelIds = Array.from(
    new Set(
      calls
        .map((call) => call.modelId)
        .filter((modelId): modelId is string =>
          typeof modelId === 'string' && modelId.includes('/') && modelId.toLowerCase() !== 'openrouter/auto'
        )
    )
  );

  if (openRouterModelIds.length === 0) {
    return calls;
  }

  let pricingMap: Map<string, OpenRouterPricingInfo>;
  try {
    pricingMap = await getOpenRouterPricingMap();
  } catch (error) {
    console.warn('Failed to load OpenRouter pricing for billing enrichment:', error);
    return calls;
  }

  return calls.map((call) => {
    const modelId = call.modelId;
    if (!modelId) {
      return call;
    }

    const existingInputPrice = call.model?.inputTokenPrice;
    const existingOutputPrice = call.model?.outputTokenPrice;
    const hasModelPricing =
      typeof existingInputPrice === 'number' && Number.isFinite(existingInputPrice) &&
      typeof existingOutputPrice === 'number' && Number.isFinite(existingOutputPrice);

    if (hasModelPricing) {
      return call;
    }

    const pricingInfo = pricingMap.get(modelId);
    if (!pricingInfo) {
      return call;
    }

    return {
      ...call,
      model: {
        ...(call.model || ({} as NonNullable<LLMCall['model']>)),
        id: call.model?.id || modelId,
        name: call.model?.name || pricingInfo.name,
        inputTokenPrice:
          typeof call.model?.inputTokenPrice === 'number'
            ? call.model.inputTokenPrice
            : (pricingInfo.inputTokenPricePerMillion ?? undefined),
        outputTokenPrice:
          typeof call.model?.outputTokenPrice === 'number'
            ? call.model.outputTokenPrice
            : (pricingInfo.outputTokenPricePerMillion ?? undefined),
        defaultServiceGroup:
          call.model?.defaultServiceGroup ||
          ((pricingInfo.outputTokenPricePerMillion || 0) > 5 ? 'premium' : 'standard'),
      },
    } as LLMCall;
  });
}

export interface CustomerBillingData {
  customerId: string;
  period: PeriodData;
  metrics: BillingMetrics;
  callsData: CustomerCallsResult;
  performance: BillingPerformanceMetrics;
  lastUpdated: Date;
}

export interface BillingComparisonData {
  current: CustomerBillingData;
  previous?: CustomerBillingData;
  trendIndicators: {
    costTrend: 'up' | 'down' | 'stable';
    callsTrend: 'up' | 'down' | 'stable';
    costChangePercent: number;
    callsChangePercent: number;
  };
}

/**
 * Calculate complete billing data for a customer and period
 * This is the main entry point for billing calculations
 */
export async function calculateCustomerBilling(
  customerId: string,
  periodType: string = getDefaultPeriodType(),
  options: {
    forecastOptions?: ForecastOptions;
    includeComparison?: boolean;
    usePreview?: boolean; // For faster loading, limit to 1000 calls
  } = {}
): Promise<BillingComparisonData> {
  
  const {
    forecastOptions = {},
    includeComparison = true,
    usePreview = false
  } = options;
  
  // Calculate current period
  const currentPeriod = calculatePeriodBounds(periodType);
  const currentBilling = await calculateSinglePeriodBilling(
    customerId,
    currentPeriod,
    forecastOptions,
    usePreview
  );
  
  let previousBilling: CustomerBillingData | undefined;
  let trendIndicators: BillingComparisonData['trendIndicators'] = {
    costTrend: 'stable',
    callsTrend: 'stable',
    costChangePercent: 0,
    callsChangePercent: 0
  };
  
  // Calculate previous period for comparison (if requested)
  if (includeComparison) {
    try {
      const previousPeriodType = getPreviousPeriodType(periodType);
      const previousPeriod = calculatePeriodBounds(previousPeriodType);
      previousBilling = await calculateSinglePeriodBilling(
        customerId,
        previousPeriod,
        forecastOptions,
        usePreview
      );
      
      trendIndicators = calculateTrendIndicators(currentBilling, previousBilling);
    } catch (error) {
      console.warn('Failed to calculate previous period comparison:', error);
      // Continue without comparison data
    }
  }
  
  return {
    current: currentBilling,
    previous: previousBilling,
    trendIndicators
  };
}

/**
 * Calculate billing data for a single period
 */
async function calculateSinglePeriodBilling(
  customerId: string,
  period: PeriodData,
  forecastOptions: ForecastOptions,
  usePreview: boolean
): Promise<CustomerBillingData> {
  
  const calculationStartTime = Date.now();
  
  // Fetch call data
  const callsData = usePreview 
    ? await fetchLLMCallsPreview(customerId, period)
    : await fetchLLMCallsByCustomerIdAndPeriod(customerId, period);

  const enrichedCalls = await enrichCallsWithOpenRouterPricing(callsData.calls);
  const enrichedCallsData: CustomerCallsResult = {
    ...callsData,
    calls: enrichedCalls,
  };
  
  // Calculate billing metrics with forecasting
  const metrics = await calculateBillingMetrics(
    enrichedCalls,
    period,
    forecastOptions
  );
  
  const calculationTimeMs = Date.now() - calculationStartTime - callsData.fetchTimeMs;
  
  // Log performance metrics
  const performanceMetrics: BillingPerformanceMetrics = {
    totalCallsProcessed: callsData.totalCount,
    fetchTimeMs: callsData.fetchTimeMs,
    calculationTimeMs,
    pagesRetrieved: callsData.pagesRetrieved,
    cacheHit: false // TODO: Implement caching in future iteration
  };
  
  logBillingPerformance(customerId, period, performanceMetrics);
  
  return {
    customerId,
    period,
    metrics,
    callsData: enrichedCallsData,
    performance: performanceMetrics,
    lastUpdated: new Date()
  };
}

/**
 * Calculate trend indicators by comparing current and previous periods
 */
function calculateTrendIndicators(
  current: CustomerBillingData,
  previous: CustomerBillingData
): BillingComparisonData['trendIndicators'] {
  
  const currentCost = current.metrics.totalProjectedCost || current.metrics.actualCost;
  const previousCost = previous.metrics.actualCost; // Previous periods are always complete
  
  const currentCalls = current.metrics.totalProjectedCalls || current.metrics.actualCalls;
  const previousCalls = previous.metrics.actualCalls;
  
  // Calculate percentage changes
  const costChangePercent = previousCost > 0 
    ? ((currentCost - previousCost) / previousCost) * 100 
    : 0;
  
  const callsChangePercent = previousCalls > 0
    ? ((currentCalls - previousCalls) / previousCalls) * 100
    : 0;
  
  // Determine trend directions (threshold: 5% change)
  const costTrend = Math.abs(costChangePercent) < 5 
    ? 'stable' 
    : costChangePercent > 0 ? 'up' : 'down';
  
  const callsTrend = Math.abs(callsChangePercent) < 5
    ? 'stable'
    : callsChangePercent > 0 ? 'up' : 'down';
  
  return {
    costTrend,
    callsTrend,
    costChangePercent: Math.round(costChangePercent * 100) / 100, // Round to 2 decimal places
    callsChangePercent: Math.round(callsChangePercent * 100) / 100
  };
}

/**
 * Get the corresponding previous period type for comparison
 */
function getPreviousPeriodType(currentPeriodType: string): string {
  switch (currentPeriodType) {
    case 'current-month':
      return 'previous-month';
    case 'current-week':
      return 'previous-week';
    case 'today':
      return 'yesterday';
    default:
      throw new Error(`Cannot determine previous period for: ${currentPeriodType}`);
  }
}

/**
 * Format cost for display with proper currency formatting
 */
export function formatCost(cost: number): string {
  if (cost === 0) return '$0.00';
  if (cost < 0.01) return '<$0.01';
  return `$${cost.toFixed(2)}`;
}

/**
 * Format large numbers with appropriate units (K, M)
 */
export function formatCallCount(count: number): string {
  if (count === 0) return '0';
  if (count < 1000) return count.toString();
  if (count < 1000000) return `${(count / 1000).toFixed(1)}K`;
  return `${(count / 1000000).toFixed(1)}M`;
}

/**
 * Format trend change for display
 */
export function formatTrendChange(changePercent: number, type: 'cost' | 'calls'): string {
  if (Math.abs(changePercent) < 0.1) return '0%';
  
  const sign = changePercent > 0 ? '+' : '';
  const formatted = `${sign}${changePercent.toFixed(1)}%`;
  
  return formatted;
}

/**
 * Get trend icon for UI display
 */
export function getTrendIcon(trend: 'up' | 'down' | 'stable'): string {
  switch (trend) {
    case 'up': return '↗️';
    case 'down': return '↘️';
    case 'stable': return '➡️';
  }
}

/**
 * Validate billing calculation inputs
 */
export function validateBillingCalculationInputs(
  customerId: string,
  periodType: string
): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  if (!customerId || customerId.trim() === '') {
    errors.push('Customer ID is required');
  }
  
  if (!periodType || periodType.trim() === '') {
    errors.push('Period type is required');
  }
  
  const validPeriodTypes = [
    'current-month', 'previous-month',
    'current-week', 'previous-week',
    'today', 'yesterday'
  ];
  
  if (!validPeriodTypes.includes(periodType)) {
    errors.push(`Invalid period type: ${periodType}. Valid types: ${validPeriodTypes.join(', ')}`);
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Calculate summary statistics for multiple customers (for admin overview)
 */
export interface BillingSummaryStats {
  totalCustomers: number;
  totalCost: number;
  totalCalls: number;
  averageCostPerCustomer: number;
  averageCallsPerCustomer: number;
  topCustomersByCost: Array<{
    customerId: string;
    cost: number;
    calls: number;
  }>;
}

export async function calculateBillingSummary(
  customerIds: string[],
  periodType: string = getDefaultPeriodType(),
  topCustomersLimit: number = 10
): Promise<BillingSummaryStats> {
  
  const customerBillingData: Array<{
    customerId: string;
    cost: number;
    calls: number;
  }> = [];
  
  // Calculate billing for each customer
  for (const customerId of customerIds) {
    try {
      const billing = await calculateCustomerBilling(customerId, periodType, {
        includeComparison: false,
        usePreview: true // Use preview for performance
      });
      
      const cost = billing.current.metrics.totalProjectedCost || billing.current.metrics.actualCost;
      const calls = billing.current.metrics.totalProjectedCalls || billing.current.metrics.actualCalls;
      
      customerBillingData.push({
        customerId,
        cost,
        calls
      });
    } catch (error) {
      console.warn(`Failed to calculate billing for customer ${customerId}:`, error);
      // Continue with other customers
    }
  }
  
  // Calculate summary statistics
  const totalCost = customerBillingData.reduce((sum, data) => sum + data.cost, 0);
  const totalCalls = customerBillingData.reduce((sum, data) => sum + data.calls, 0);
  const totalCustomers = customerBillingData.length;
  
  const averageCostPerCustomer = totalCustomers > 0 ? totalCost / totalCustomers : 0;
  const averageCallsPerCustomer = totalCustomers > 0 ? totalCalls / totalCustomers : 0;
  
  // Sort by cost and get top customers
  const topCustomersByCost = customerBillingData
    .sort((a, b) => b.cost - a.cost)
    .slice(0, topCustomersLimit);
  
  return {
    totalCustomers,
    totalCost,
    totalCalls,
    averageCostPerCustomer,
    averageCallsPerCustomer,
    topCustomersByCost
  };
}
